/**
 * Hub sidebar for admin routes.
 * Shows "Admin Hub" header, a "Back to workspace" link, and all admin nav items.
 * Mirrors AppSidebar visual design (floating card, same classes).
 */

import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorkspaceUrl, adminRoutes, CHEF_ENABLED } from "../../lib/routes";
import { SidebarFooter } from "./SidebarFooter";
import {
  Users,
  Building2,
  FileText,
  Layers,
  CookingPot,
  Wand2,
  Settings,
  Network,
  Workflow,
  Zap,
  BarChart3,
  CreditCard,
  Timer,
  Activity,
  Mic,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";

interface HubSidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function HubSidebar({
  collapsed = false,
  onToggle,
  mobileOpen = false,
  onMobileClose: _onMobileClose,
}: HubSidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const urls = useWorkspaceUrl();

  const backPath = urls?.dashboard || "/";

  const isActive = (path: string) => location.pathname.startsWith(path);

  const adminItems = [
    { label: t("admin.users"), path: adminRoutes.users, icon: Users },
    { label: t("admin.workspaces"), path: adminRoutes.workspaces, icon: Building2 },
    { label: t("admin.documentRecipes"), path: adminRoutes.documentRecipes, icon: FileText },
    { label: t("admin.projectTemplates"), path: adminRoutes.projectTemplates, icon: Layers },
    ...(CHEF_ENABLED ? [{ label: t("nav.recipeChef"), path: adminRoutes.chef, icon: CookingPot }] : []),
    { label: t("admin.wizards"), path: adminRoutes.wizards, icon: Wand2 },
    { label: t("admin.boostUsage"), path: adminRoutes.boostUsage, icon: Zap },
    { label: t("admin.activity"), path: adminRoutes.activity, icon: Activity },
    { label: t("admin.boostPricing"), path: adminRoutes.boostPricing, icon: BarChart3 },
    { label: t("admin.stripeProducts"), path: adminRoutes.stripeProducts, icon: CreditCard },
    { label: t("admin.trials"), path: adminRoutes.trials, icon: Timer },
    { label: t("admin.workflows"), path: adminRoutes.workflows, icon: Mic },
    { label: t("admin.settings"), path: adminRoutes.settings, icon: Settings },
    { label: t("admin.infrastructure"), path: adminRoutes.infrastructure, icon: Network },
    { label: t("admin.architecture"), path: adminRoutes.architecture, icon: Workflow },
  ];

  return (
    <aside
      className={[
        "fixed top-[18px] left-[18px] bottom-[18px] bg-card rounded-2xl z-50 flex flex-col transition-all duration-300 overflow-y-auto lg:overflow-y-hidden",
        mobileOpen ? "translate-x-0" : "-translate-x-[calc(100%+18px)]",
        "lg:translate-x-0 lg:z-40",
        collapsed ? "sidebar-collapsed" : "sidebar-expanded",
      ].join(" ")}
    >
      {/* Header */}
      <div className="sidebar-header-content flex items-center h-16 px-6">
        <span className="sidebar-text text-lg font-bold tracking-tight text-foreground whitespace-nowrap">
          Admin Hub
        </span>
        <button
          onClick={onToggle}
          className="ml-auto p-2 hover:bg-muted/50 rounded-xl text-muted-foreground hidden lg:block shrink-0"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {/* Back to workspace */}
        <ul className="space-y-1 mb-4">
          <li>
            <Link
              to={backPath}
              className="nav-item flex items-center gap-3.5 px-4 py-3 text-[0.9375rem] rounded-xl transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/50"
              title={collapsed ? "Back to workspace" : undefined}
            >
              <ArrowLeft className="h-[1.2rem] w-[1.2rem] shrink-0" />
              <span className="sidebar-text">Back to workspace</span>
            </Link>
          </li>
        </ul>

        <div className="section-divider h-px bg-border/50 mx-4 mb-4"></div>

        {/* Admin items */}
        <ul className="space-y-1">
          {adminItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`nav-item flex items-center gap-3.5 px-4 py-3 text-[0.9375rem] rounded-xl transition-colors ${
                  isActive(item.path)
                    ? "bg-nara-orange text-nara-charcoal font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="h-[1.2rem] w-[1.2rem] shrink-0" />
                <span className="sidebar-text">{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <SidebarFooter />
    </aside>
  );
}
